import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { getFirestore } from 'firebase/firestore';
import { format2 } from "./utils/format2";
import styles from './SearchBar.module.css';

const SearchBar = ({ placeholder = "Search products, ingredients, recipes, stores, items, vendors..." }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [allData, setAllData] = useState({
    products: [],
    ingredients: [],
    recipes: [],
    stores: [],
    items: [],
    vendors: []
  });
  
  const searchRef = useRef(null);
  const resultsRef = useRef(null);
  const navigate = useNavigate();
  const db = getFirestore();

  // Fetch data when component mounts
  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setLoading(true);
        
        const [productsSnap, ingredientsSnap, storesSnap, itemsSnap, vendorsSnap] = await Promise.all([
          getDocs(collection(db, 'products')),
          getDocs(collection(db, 'ingredients')),
          getDocs(collection(db, 'stores')),
          getDocs(collection(db, 'items')),
          getDocs(collection(db, 'vendors'))
        ]);

        const products = productsSnap.docs.map(doc => ({
          id: doc.id,
          type: 'product',
          ...doc.data()
        }));

        const stores = storesSnap.docs.map(doc => ({
          id: doc.id,
          type: 'store',
          ...doc.data()
        }));

        // Map store names to ingredients
        const ingredients = ingredientsSnap.docs.map(doc => {
          const data = doc.data();
          const store = stores.find(s => s.id === data.storeId);
          return {
            id: doc.id,
            type: 'ingredient',
            ...data,
            storeName: store?.name || 'Unknown Store'
          };
        });

        // Extract recipes from products and count ingredients
        const recipes = [];
        products.forEach(product => {
          if (product.recipes) {
            product.recipes.forEach((recipe, index) => {
              recipes.push({
                id: `${product.id}-${index}`,
                type: 'recipe',
                recipeName: recipe.recipeName,
                productName: product.name,
                productId: product.id,
                recipeIndex: index,
                isFinal: recipe.isFinal || false,
                ingredientCount: recipe.ingredients ? recipe.ingredients.length : 0
              });
            });
          }
        });

        // Add ingredient counts to stores
        const storesWithCounts = stores.map(store => ({
          ...store,
          ingredientCount: ingredients.filter(ing => ing.storeId === store.id).length
        }));

        // Process vendors
        const vendors = vendorsSnap.docs.map(doc => ({
          id: doc.id,
          type: 'vendor',
          ...doc.data()
        }));

        // Map vendor names to items and add counts
        const items = itemsSnap.docs.map(doc => {
          const data = doc.data();
          const vendor = vendors.find(v => v.id === data.vendorId);
          return {
            id: doc.id,
            type: 'item',
            ...data,
            vendorName: vendor?.name || 'Unknown Vendor'
          };
        });

        // Add item counts to vendors
        const vendorsWithCounts = vendors.map(vendor => ({
          ...vendor,
          itemCount: items.filter(item => item.vendorId === vendor.id).length
        }));

        setAllData({ products, ingredients, recipes, stores: storesWithCounts, items, vendors: vendorsWithCounts });
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, [db]);

  // Search function
  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      setSelectedIndex(0);
      setIsOpen(false);
      return;
    }

    const searchTerm = query.toLowerCase();
    const searchResults = [];

    // Search products
    allData.products.forEach(product => {
      if (product.name?.toLowerCase().includes(searchTerm)) {
        searchResults.push({
          ...product,
          title: product.name,
          subtitle: 'Product',
          icon: '📦'
        });
      }
    });

    // Search ingredients
    allData.ingredients.forEach(ingredient => {
      if (ingredient.name?.toLowerCase().includes(searchTerm)) {
        const currentStock = ingredient.currentStock || 0;
        const unit = ingredient.unit || 'units';
        searchResults.push({
          ...ingredient,
          title: ingredient.name,
          subtitle: `Ingredient • ${ingredient.storeName || 'Unknown store'} • Stock: ${format2(currentStock)} ${unit}`,
          icon: '🥄'
        });
      }
    });

    // Search recipes
    allData.recipes.forEach(recipe => {
      if (recipe.recipeName?.toLowerCase().includes(searchTerm) || 
          recipe.productName?.toLowerCase().includes(searchTerm)) {
        const ingredientText = recipe.ingredientCount === 1 ? '1 ingredient' : `${recipe.ingredientCount} ingredients`;
        searchResults.push({
          ...recipe,
          title: recipe.recipeName,
          subtitle: `Recipe • ${recipe.productName}${recipe.isFinal ? ' (Final)' : ''} • ${ingredientText}`,
          icon: '📋'
        });
      }
    });

    // Search stores
    allData.stores.forEach(store => {
      if (store.name?.toLowerCase().includes(searchTerm)) {
        const ingredientText = store.ingredientCount === 1 ? '1 ingredient' : `${store.ingredientCount} ingredients`;
        searchResults.push({
          ...store,
          title: store.name,
          subtitle: `Store • ${ingredientText}`,
          icon: '🏪'
        });
      }
    });

    // Search items
    allData.items.forEach(item => {
      if (item.name?.toLowerCase().includes(searchTerm) || 
          item.vendorName?.toLowerCase().includes(searchTerm) ||
          item.category?.toLowerCase().includes(searchTerm)) {
        const currentStock = item.currentStock || 0;
        const unit = item.unit || 'units';
        searchResults.push({
          ...item,
          title: item.name,
          subtitle: `Item • ${item.vendorName || 'Unknown vendor'} • Stock: ${format2(currentStock)} ${unit}`,
          icon: '📦'
        });
      }
    });

    // Search vendors
    allData.vendors.forEach(vendor => {
      if (vendor.name?.toLowerCase().includes(searchTerm)) {
        const itemText = vendor.itemCount === 1 ? '1 item' : `${vendor.itemCount} items`;
        searchResults.push({
          ...vendor,
          title: vendor.name,
          subtitle: `Vendor • ${itemText}`,
          icon: '🏢'
        });
      }
    });

    // Sort and limit results
    searchResults.sort((a, b) => {
      const aExact = a.title.toLowerCase() === searchTerm;
      const bExact = b.title.toLowerCase() === searchTerm;
      
      if (aExact && !bExact) return -1;
      if (!aExact && bExact) return 1;
      
      const aStarts = a.title.toLowerCase().startsWith(searchTerm);
      const bStarts = b.title.toLowerCase().startsWith(searchTerm);
      
      if (aStarts && !bStarts) return -1;
      if (!aStarts && bStarts) return 1;
      
      return a.title.localeCompare(b.title);
    });

    setResults(searchResults.slice(0, 8));
    setSelectedIndex(0);
    setIsOpen(true);
  }, [query, allData]);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen || results.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < results.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : prev);
        break;
      case 'Enter':
        e.preventDefault();
        if (results[selectedIndex]) {
          handleItemSelect(results[selectedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        searchRef.current?.blur();
        break;
      default:
        break;
    }
  };

  // Handle item selection
  const handleItemSelect = (item) => {
    setQuery('');
    setIsOpen(false);
    setResults([]);
    searchRef.current?.blur();
    
    switch (item.type) {
      case 'product':
        navigate(`/products/${item.id}`);
        break;
      case 'ingredient':
        navigate('/ingredients', { 
          state: { highlightId: item.id, highlightName: item.name } 
        });
        break;
      case 'recipe':
        navigate(`/products/${item.productId}`, { 
          state: { highlightRecipe: item.recipeIndex, recipeName: item.title } 
        });
        break;
      case 'store':
        navigate('/stores', { 
          state: { highlightId: item.id, highlightName: item.name } 
        });
        break;
      case 'item':
        navigate('/items', { 
          state: { highlightId: item.id, highlightName: item.name } 
        });
        break;
      case 'vendor':
        navigate('/vendors', { 
          state: { highlightId: item.id, highlightName: item.name } 
        });
        break;
      default:
        break;
    }
  };

  // Close results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={styles.searchContainer} ref={searchRef}>
      <div className={styles.searchInputContainer}>
        <div className={styles.searchIcon}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"></circle>
            <path d="m21 21-4.35-4.35"></path>
          </svg>
        </div>
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => query && setIsOpen(true)}
          placeholder={placeholder}
          className={styles.searchInput}
        />
        {loading && (
          <div className={styles.loadingSpinner}>
            <svg className={styles.spinner} viewBox="0 0 24 24">
              <circle className={styles.spinnerPath} cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2"/>
            </svg>
          </div>
        )}
        {query && (
          <button
            onClick={() => {
              setQuery('');
              setIsOpen(false);
              setResults([]);
            }}
            className={styles.clearButton}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        )}
      </div>

      {isOpen && results.length > 0 && (
        <div className={styles.resultsContainer} ref={resultsRef}>
          {results.map((result, index) => (
            <div
              key={`${result.type}-${result.id}`}
              className={`${styles.resultItem} ${
                index === selectedIndex ? styles.selected : ''
              }`}
              onClick={() => handleItemSelect(result)}
              onMouseEnter={() => setSelectedIndex(index)}
            >
              <div className={styles.resultIcon}>{result.icon}</div>
              <div className={styles.resultContent}>
                <div className={styles.resultTitle}>{result.title}</div>
                <div className={styles.resultSubtitle}>{result.subtitle}</div>
              </div>
              {result.type === 'recipe' && result.isFinal && (
                <div className={styles.finalBadge}>FINAL</div>
              )}
            </div>
          ))}
        </div>
      )}

      {isOpen && query && results.length === 0 && !loading && (
        <div className={styles.noResults}>
          <div className={styles.noResultsIcon}>🔍</div>
          <div className={styles.noResultsText}>No results found</div>
        </div>
      )}
    </div>
  );
};

export default SearchBar;
